// The quiz's own local memory: answers stay editable and un-synced to the
// server (only the derived conclusion is — see checkpoint-sync.ts), so
// they live in localStorage, one entry per quote. Every accessor is
// wrapped against a throwing or unavailable Storage (private browsing,
// cleared site data) — a quiz that can't remember its screen just starts
// over, it never breaks.

import type { Answers } from "./types";

interface StoredQuiz {
  screen: string;
  answers: Answers;
}

function storageKey(quoteId: string): string {
  return `after.quote_quiz.${quoteId}`;
}

export function loadQuiz(quoteId: string): StoredQuiz | null {
  try {
    const raw = window.localStorage.getItem(storageKey(quoteId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof (parsed as StoredQuiz).screen !== "string" ||
      typeof (parsed as StoredQuiz).answers !== "object" ||
      (parsed as StoredQuiz).answers === null
    ) {
      return null;
    }
    return parsed as StoredQuiz;
  } catch {
    return null;
  }
}

export function saveQuiz(quoteId: string, quiz: StoredQuiz): void {
  try {
    window.localStorage.setItem(storageKey(quoteId), JSON.stringify(quiz));
  } catch {
    // Nothing on hand to fall back to — the next answer just tries again.
  }
}

export function clearQuiz(quoteId: string): void {
  try {
    window.localStorage.removeItem(storageKey(quoteId));
  } catch {
    // Already unreadable, so nothing to clear either.
  }
}

/** Whether this device holds any quiz progress for the quote — the
 * signal the landing rule uses to decide whether a `quiz` request past
 * the derivation may still render (editable) or must fall back to the
 * derived station. */
export function hasStoredAnswers(quoteId: string): boolean {
  const stored = loadQuiz(quoteId);
  return stored !== null && Object.keys(stored.answers).length > 0;
}

export type { StoredQuiz };
